import React, { useState, useEffect, useContext, forwardRef, useImperativeHandle } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Box
} from '@mui/material';
import { Row, Col, Label, FormGroup, Form } from 'reactstrap';
import { useForm, useController } from 'react-hook-form';
import CustomSelect from "../../../CommonElements/CustomSelect";
import DatePicker from 'react-datepicker';
import moment from "moment";
import { addSeconds } from "date-fns";
import { showAlert } from '../../../Services/Shared.service';
import { createDriverPayout, updateDriverPayout, getPayoutAllowedCities } from "../../../Services/Payout.service.jsx";
import { getCarTypes } from "../../../Services/Shared.service";
import SharedDataContext from "../../../_helper/SharedData";
import { useDispatch } from 'react-redux';
import { fetchDriverPayouts } from '../../../redux/slices/tariffSlice';

const SECONDS_IN_DAY = 86400;

const CreateEditPayoutForm = forwardRef(({ open, onClose, onSuccess, editData, standalone = false, disableServiceCity = false }, ref) => {
    const { cityData } = useContext(SharedDataContext);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    
    // Dropdown Data States
    const [serviceCities, setServiceCities] = useState([]);
    const [carTypes, setCarTypes] = useState([]);
    const [loadingCarTypes, setLoadingCarTypes] = useState(false);

    const { register, control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
        mode: 'onChange',
        defaultValues: {
            payoutName: '',
            serviceCities: null, 
            carTypes: null,
            initialDistance: '',
            charge: '',
            perKmCharge: '',
            timeFrom: null,
            timeTo: null,
        }
    });

    // Fetch payout enabled cities from API and filter against context
    useEffect(() => {
        const fetchCities = async () => {
            try {
                const resp = await getPayoutAllowedCities();
                const allowedCityIds = resp?.data?.list || resp?.data || [];

                if (cityData && cityData.length > 0) {
                    const filteredCities = cityData.filter(city => allowedCityIds.includes(city.id));
                    setServiceCities(filteredCities.map(c => ({ value: c.id, label: c.label })));
                } else {
                    setServiceCities([]);
                }
            } catch (error) {
                console.error("Error fetching payout cities:", error);
                setServiceCities([]);
                showAlert('error', 'Failed to load payout cities');
            }
        };

        // Only attempt to fetch and filter if cityData from context is available
        if (cityData && cityData.length > 0) {
            fetchCities();
        }
    }, [cityData]);

    // Watch Service City change to fetch Car Types
    const selectedCity = watch("serviceCities");

    useEffect(() => {
        const fetchCars = async () => {
            if (!selectedCity?.value) {
                setCarTypes([]);
                return;
            }

            setLoadingCarTypes(true);
            try {
                const resp = await getCarTypes({ serviceCityId: selectedCity.value });
                const cars = resp?.data?.list || resp?.data || [];
                setCarTypes(cars.map(c => ({ value: c.id, label: c.name })));
            } catch (error) {
                console.error("Error fetching car types:", error);
                setCarTypes([]);
                showAlert('error', 'Failed to load car types');
            } finally {
                setLoadingCarTypes(false);
            }
        };
        
        fetchCars();
    }, [selectedCity]);

    // Reset form when dialog opens in create mode
    useEffect(() => {
        if (!open) return;
        
        if (!editData) {
            reset({
                payoutName: '',
                serviceCities: null,
                carTypes: null,
                initialDistance: '',
                charge: '', 
                perKmCharge: '',
                timeFrom: null,
                timeTo: null,
            });
        }
    }, [open, editData, reset]);

    // Populate form for edit mode when data is available
    useEffect(() => {
        if (!editData || serviceCities.length === 0) return;
        
        const cityOption = serviceCities.find(c => c.value === editData.serviceCityId);
        if (cityOption) {
            reset({
                payoutName: editData.name || '',
                serviceCities: cityOption,
                carTypes: null,
                initialDistance: editData.initialDistance || '',
                charge: editData.initialDistanceCharge || '', 
                perKmCharge: editData.perKmCharge || '',
                timeFrom: (editData.timeFrom !== null && editData.timeFrom !== undefined) 
                    ? addSeconds(new Date().setHours(0, 0, 0, 0), editData.timeFrom) 
                    : null,
                timeTo: (editData.timeTo !== null && editData.timeTo !== undefined) 
                    ? addSeconds(new Date().setHours(0, 0, 0, 0), editData.timeTo) 
                    : null,
            });
        }
    }, [editData, serviceCities, reset]);

    useEffect(() => {
        if (!editData || carTypes.length === 0) return;
        
        const carTypeOption = carTypes.find(c => c.value === editData.carTypeId);
        if (carTypeOption) {
            setValue('carTypes', carTypeOption);
        }
    }, [editData, carTypes, setValue]);

    // Expose submit method to parent via ref
    useImperativeHandle(ref, () => ({
        submit: () => {
            return handleSubmit(handleFormSubmit)();
        }
    }));

    const serviceCitiesField = useController({
        name: 'serviceCities',
        control,
        rules: { required: { value: true, message: 'Service City is required' } },
    }).field;

    const carTypesField = useController({
        name: 'carTypes',
        control,
        rules: { required: { value: true, message: 'Car Type is required' } }
    }).field;

    const timeFromField = useController({
        name: 'timeFrom',
        control,
        rules: { required: { value: true, message: 'Time From is required' } }
    }).field;

    const timeToField = useController({
        name: 'timeTo',
        control,
        rules: {
            required: { value: true, message: 'Time To is required' },
            validate: (value) => {
                const timeFrom = watch('timeFrom');
                if (!value || !timeFrom) return true;

                const timeFromSeconds = Number(moment.duration(moment(timeFrom).format("HH:mm:ss")).asSeconds());
                let timeToSeconds = Number(moment.duration(moment(value).format("HH:mm:ss")).asSeconds());

                if (timeToSeconds === 0) timeToSeconds = SECONDS_IN_DAY;

                return timeToSeconds > timeFromSeconds || 'End time must be greater than start time';
            }
        }
    }).field;

    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingData, setPendingData] = useState(null);

    const handleFormSubmit = async (data) => {
        if (editData?.id && editData.connectedPackages > 0) {
            setPendingData(data);
            setShowConfirm(true);
            return;
        }
        await processSubmit(data);
    };

    const processSubmit = async (data) => {
        if (loading) return;
        setLoading(true);
        try {
            const payload = {
                name: data.payoutName,
                serviceCityId: data.serviceCities?.value, 
                carTypeId: data.carTypes?.value,
                initialDistance: parseFloat(data.initialDistance),
                initialDistanceCharge: parseFloat(data.charge),
                perKmCharge: parseFloat(data.perKmCharge),
                timeFrom: data.timeFrom 
                    ? Number(moment.duration(moment(data.timeFrom).format("HH:mm:ss")).asSeconds()) 
                    : 0,
                timeTo: data.timeTo 
                    ? (Number(moment.duration(moment(data.timeTo).format("HH:mm:ss")).asSeconds()) === 0 
                        ? SECONDS_IN_DAY 
                        : Number(moment.duration(moment(data.timeTo).format("HH:mm:ss")).asSeconds()))
                    : SECONDS_IN_DAY,
            };
            
            if (editData?.id) {
                await updateDriverPayout(editData.id, payload);
                showAlert('success', 'Payout updated successfully');
            } else {
                await createDriverPayout(payload);
                showAlert('success', 'Payout created successfully');
            }
            
            // Refresh Redux payout list
            dispatch(fetchDriverPayouts(true));
            
            onSuccess();
            setShowConfirm(false);
            
            // Only close if in standalone mode
            if (standalone) {
                onClose();
            }
        } catch (error) {
            console.error('Form submission error:', error);
            showAlert('error', error.response?.data?.message || 'Operation failed');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        reset();
        setCarTypes([]);
        onClose();
    };

    const confirmDialog = (
        <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
            <DialogTitle>Confirm Update</DialogTitle>
            <DialogContent>
                This payout is connected to {editData?.connectedPackages} package(s). Updating it will affect all connected packages. Do you want to proceed?
            </DialogContent>
            <DialogActions>
                <button 
                  onClick={() => setShowConfirm(false)} 
                  className="btn"
                  style={{ backgroundColor: "white", color: "#666", border: "1px solid #d9d9d9", padding: "8px 20px", borderRadius: "6px" }}
                >
                    Cancel
                </button>
                <button 
                    onClick={() => processSubmit(pendingData)} 
                    className="btn"
                    style={{ backgroundColor: "rgba(57, 181, 74, 1)", color: "white", border: "none", padding: "8px 30px", borderRadius: "6px" }}
                >
                    Confirm
                </button>
            </DialogActions>
        </Dialog>
    );

    const formContent = (
        <Box sx={{ mt: 2 }}>
            {confirmDialog}
            <Row>
                <FormGroup className="col-lg-4">
                    <Label className="col-lg-12 col-form-label">Payout Name <span className="text-danger">*</span></Label>
                    <Col lg="12">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Payout Name"
                            {...register("payoutName", {
                                required: { value: true, message: 'Payout Name is required' },
                                maxLength: { value: 100, message: 'Name cannot exceed 100 characters' }
                            })}
                        />
                        <span style={{ color: "red" }}>{errors?.payoutName?.message}</span>
                    </Col>
                </FormGroup>

                <FormGroup className="col-lg-4">
                    <Label className="col-lg-12 col-form-label">Service City <span className="text-danger">*</span></Label>
                    <Col lg="12">
                        <CustomSelect
                            className="service-select"
                            classNamePrefix="select"
                            isClearable={!disableServiceCity}
                            isSearchable={true}
                            value={serviceCitiesField.value}
                            onChange={(value) => {
                                serviceCitiesField.onChange(value);
                                setValue("carTypes", null);
                                setCarTypes([]);
                            }}
                            options={serviceCities}
                            placeholder="Select City"
                            isDisabled={disableServiceCity}
                        />
                        <span style={{ color: "red" }}>{errors?.serviceCities?.message}</span>
                    </Col>
                </FormGroup>

                <FormGroup className="col-lg-4">
                    <Label className="col-lg-12 col-form-label">Car Type <span className="text-danger">*</span></Label>
                    <Col lg="12">
                        <CustomSelect
                            className="service-select"
                            classNamePrefix="select"
                            isClearable={true}
                            isSearchable={true}
                            value={carTypesField.value}
                            onChange={(value) => carTypesField.onChange(value)}
                            options={carTypes}
                            placeholder="Select Car Type"
                            isLoading={loadingCarTypes}
                            isDisabled={!selectedCity}
                        />
                        <span style={{ color: "red" }}>{errors?.carTypes?.message}</span>
                    </Col>
                </FormGroup>
            </Row>

            <Row>
                <FormGroup className="col-lg-4">
                    <Label className="col-lg-12 col-form-label">Initial Distance (Km) <span className="text-danger">*</span></Label>
                    <Col lg="12">
                        <input
                            type="number"
                            className="form-control"
                            placeholder="e.g. 10"
                            min="0"
                            step="0.01"
                            onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                            onInput={(e) => {
                                if (e.target.value.length > 5) e.target.value = e.target.value.slice(0, 5);
                            }}
                            {...register("initialDistance", {
                                required: { value: true, message: 'Initial Distance is required' },
                                min: { value: 0, message: 'Cannot be negative' }
                            })}
                        />
                        <span style={{ color: "red" }}>{errors?.initialDistance?.message}</span>
                    </Col>
                </FormGroup>

                <FormGroup className="col-lg-4">
                    <Label className="col-lg-12 col-form-label">Initial Distance Charge <span className="text-danger">*</span></Label>
                    <Col lg="12">
                        <input
                            type="number"
                            className="form-control"
                            placeholder="e.g. 500"
                            min="0"
                            step="0.01"
                            onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                            onInput={(e) => {
                                if (e.target.value.length > 5) e.target.value = e.target.value.slice(0, 5);
                            }}
                            {...register("charge", {
                                required: { value: true, message: 'Charge is required' },
                                min: { value: 0, message: 'Cannot be negative' }
                            })}
                        />
                        <span style={{ color: "red" }}>{errors?.charge?.message}</span>
                    </Col>
                </FormGroup>

                <FormGroup className="col-lg-4">
                    <Label className="col-lg-12 col-form-label">Per Km Charge <span className="text-danger">*</span></Label>
                    <Col lg="12">
                        <input
                            type="number"
                            className="form-control"
                            placeholder="e.g. 12"
                            min="0"
                            step="0.01"
                            onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                            onInput={(e) => {
                                if (e.target.value.length > 5) e.target.value = e.target.value.slice(0, 5);
                            }}
                            {...register("perKmCharge", {
                                required: { value: true, message: 'Per Km Charge is required' },
                                min: { value: 0, message: 'Cannot be negative' }
                            })}
                        />
                        <span style={{ color: "red" }}>{errors?.perKmCharge?.message}</span>
                    </Col>
                </FormGroup>
            </Row>

            <Row>
                <FormGroup className="col-lg-4">
                    <Label className="col-lg-12 col-form-label">Time From <span className="text-danger">*</span></Label>
                    <Col lg="12">
                        <DatePicker
                            selected={timeFromField.value}
                            onChange={timeFromField.onChange}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={60}
                            timeCaption="Time"
                            dateFormat="h:mm aa"
                            timeFormat="h:mm aa"
                            className="form-control datetimepicker-input digits"
                            placeholderText="Select Time From"
                            popperPlacement="top-start"
                            popperProps={{ style: { maxHeight: '50px' } }}
                            onKeyDown={(e) => {
                                if (e.key !== 'Tab') e.preventDefault();
                            }}
                        />
                        <span style={{ color: "red" }}>{errors?.timeFrom?.message}</span>
                    </Col>
                </FormGroup>

                <FormGroup className="col-lg-4">
                    <Label className="col-lg-12 col-form-label">Time To <span className="text-danger">*</span></Label>
                    <Col lg="12">
                        <DatePicker
                            selected={timeToField.value}
                            onChange={timeToField.onChange}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={60}
                            timeCaption="Time"
                            dateFormat="h:mm aa"
                            timeFormat="h:mm aa"
                            className="form-control datetimepicker-input digits"
                            placeholderText="Select Time To"
                            popperPlacement="top-start"
                            popperProps={{ style: { maxHeight: '50px' } }}
                            onKeyDown={(e) => {
                                if (e.key !== 'Tab') e.preventDefault();
                            }}
                        />
                        <span style={{ color: "red" }}>{errors?.timeTo?.message}</span>
                    </Col>
                </FormGroup>
            </Row>
        </Box>
    );

    if (standalone) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    {editData?.id ? 'Edit Payout' : 'Create Payout'}
                </DialogTitle>
                <DialogContent dividers className="p-0">
                    <Form onSubmit={handleSubmit(handleFormSubmit)} className="p-3">
                        {formContent}
                    </Form>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end", gap: 1 }}>
                    <button 
                        onClick={handleClose} 
                        disabled={loading} 
                        type="button" 
                        className="btn"
                        style={{ backgroundColor: "white", color: "#666", border: "1px solid #d9d9d9", padding: "8px 20px", borderRadius: "6px" }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit(handleFormSubmit)}
                        disabled={loading}
                        type="button"
                        className="btn"
                        style={{ backgroundColor: "rgba(57, 181, 74, 1)", color: "white", border: "none", padding: "8px 30px", borderRadius: "6px" }}
                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            editData?.id ? 'Update' : 'Create'
                        )}
                    </button>
                </DialogActions>
            </Dialog>
        );
    }

    // If not standalone (used within PayoutForm tabs), render just the form content
    return (
        <Form onSubmit={handleSubmit(handleFormSubmit)} className="p-3">
            {formContent}
        </Form>
    );
});

CreateEditPayoutForm.displayName = 'CreateEditPayoutForm';

export default CreateEditPayoutForm;
